import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Eye, EyeOff } from "lucide-react";
import { useAuth } from "../AuthContext";
import { apiError } from "../api";
import { Button, Input, Field } from "../components/ui";

const HERO = "https://images.pexels.com/photos/36112907/pexels-photo-36112907.jpeg";

export default function Login() {
  const { login, logoUrl } = useAuth();
  const navigate = useNavigate();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [show, setShow] = useState(false);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      await login(username.trim(), password);
      navigate("/dashboard");
    } catch (err) {
      setError(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex min-h-screen">
      <div className="relative hidden w-1/2 lg:block">
        <img src={HERO} alt="Maritime" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-navy-dark/80" />
        <div className="relative z-10 flex h-full flex-col justify-between p-12">
          <div />
          <div>
            <h1 className="font-heading text-4xl font-extrabold leading-tight text-white xl:text-5xl">
              Seafarer medical<br />verification,<br />
              <span className="text-teal">verified &amp; secured.</span>
            </h1>
            <p className="mt-6 max-w-md text-sm leading-relaxed text-slate-300">
              MedVerifys connects clinics, shipping companies, doctors and accounts on one
              secure platform for the complete PEME workflow.
            </p>
          </div>
        </div>
      </div>

      <div className="flex w-full items-center justify-center bg-slate-50 px-6 lg:w-1/2">
        <div className="w-full max-w-md fade-up">
          <div className="rounded-xl border border-slate-200 bg-white p-10">
            {logoUrl && (
              <img src={logoUrl} alt="MedVerifys" className="mx-auto mb-6 h-24 object-contain" data-testid="login-logo" />
            )}
            <h2 className="font-heading text-2xl font-extrabold text-slate-900">Sign in</h2>
            <p className="mt-1 text-sm text-slate-500">Use the credentials provided by your administrator.</p>

            <form onSubmit={submit} className="mt-8 space-y-5">
              <Field label="Username" required>
                <Input
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  placeholder="e.g. reception"
                  required
                  autoFocus
                  data-testid="login-username-input"
                />
              </Field>
              <Field label="Password" required>
                <div className="relative">
                  <Input
                    type={show ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Your password"
                    required
                    data-testid="login-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShow(!show)}
                    className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 transition-colors hover:text-slate-600"
                    data-testid="login-toggle-password"
                  >
                    {show ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </Field>
              {error && (
                <p className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700" data-testid="login-error">
                  {error}
                </p>
              )}
              <Button type="submit" size="lg" className="w-full" disabled={busy} data-testid="login-submit-btn">
                {busy ? "Signing in..." : "Sign in"}
              </Button>
            </form>
            <p className="mt-6 text-center text-xs text-slate-400">
              Lost your password? Contact your administrator for a reset.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
